import React, { type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { expValEquals } from '@confluence/feature-experiments';

import { RowInteractionWrapper } from '../../../RowInteractionWrapper.tsx';
import { useExpandIconRenderer } from '../../../hooks/useExpandIconRenderer.tsx';

import { PrincipalDetails, type PrincipalDetailsProps } from './PrincipalDetails.tsx';
import { PrincipalAccess, type PrincipalAccessProps } from './PrincipalAccess.tsx';

const i18n = defineMessages({
	generalAccessEditInfoMessage: {
		id: 'share-and-restrict-dialog.specific-access-row.general-access-edit-info',
		defaultMessage: 'General access allows anyone in the space to edit.',
		description:
			'Tooltip message shown when a user has view-only access but general access allows editing',
	},
	accessInfoIconLabel: {
		id: 'share-and-restrict-dialog.specific-access-row.access-info-icon-label',
		defaultMessage: 'Access information',
		description: 'Accessibility label for the information icon showing access level difference',
	},
});

const inlineStyle = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	':hover': {
		background: token('elevation.surface.hovered'),
	},
	borderRadius: 'radius.small',
});

export type SpecificAccessRowProps = {
	key: string;
	principalDetails: PrincipalDetailsProps;
	principalAccess: PrincipalAccessProps;
	isExpanded: boolean;
	isSpecificAccessDialogOpen: boolean;
	onExpandClick: () => void;
	onOpenSpecificAccessDialog: () => void;
	showLowerAccessTooltip?: boolean;
};

export const SpecificAccessRow: FC<SpecificAccessRowProps> = ({
	principalDetails,
	principalAccess,
	isExpanded,
	isSpecificAccessDialogOpen,
	onExpandClick,
	onOpenSpecificAccessDialog,
	showLowerAccessTooltip,
}) => {
	const { formatMessage } = useIntl();
	const { renderIcon } = useExpandIconRenderer({
		isExpanded,
		isSpecificAccessDialogOpen,
		fallbackComponent: <PrincipalAccess {...principalAccess} />,
	});

	const renderAccessInfoTooltip = () => {
		if (!showLowerAccessTooltip) {
			return null;
		}
		return (
			<Tooltip content={formatMessage(i18n.generalAccessEditInfoMessage)} position="top">
				<InformationCircleIcon label={formatMessage(i18n.accessInfoIconLabel)} size="small" />
			</Tooltip>
		);
	};

	if (!expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')) {
		return (
			<Inline grow="fill" spread="space-between" alignBlock="center" space="space.150">
				<PrincipalDetails {...principalDetails} />
				<PrincipalAccess {...principalAccess} />
			</Inline>
		);
	}

	return (
		<RowInteractionWrapper
			isExpanded={isExpanded}
			onExpandClick={onExpandClick}
			onOpenSpecificAccessDialog={onOpenSpecificAccessDialog}
			isSpecificAccessDialogOpen={isSpecificAccessDialogOpen}
		>
			<Inline
				grow="fill"
				spread="space-between"
				alignBlock="center"
				space="space.150"
				xcss={!isSpecificAccessDialogOpen && inlineStyle}
			>
				<PrincipalDetails {...principalDetails} />
				<Inline alignBlock="center" space="space.100" spread="space-between">
					{renderAccessInfoTooltip()}
					{renderIcon()}
				</Inline>
			</Inline>
		</RowInteractionWrapper>
	);
};
